import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import {
  createTelephonyUrlEndpoint,
  TELEPHONY_SYSTEMS,
  toDate
} from '../../services/telephonyService';

/**
 * Assistent für Telefonanlagen, die bei einem Anruf selbst eine Adresse aufrufen
 * (Ingest-Klasse 1): Swyx / Telekom Octopus NetPhone und andere Anlagen.
 * Ein Durchlauf legt einen Anschluss für EINEN Platz an; bei mehreren Plätzen
 * (Gruppenruf, alle klingeln) den Assistenten je Platz wiederholen.
 *
 * Konzept: nextcab_telephony_connector/docs/KONZEPT_ANRUFERKENNUNG_WEITERE_TELEFONANLAGEN.md 5.1
 */

const STEPS = ['Voraussetzungen', 'Platz anlegen', 'Adressen eintragen', 'Testanruf'];

// Bekannte Variablen anderer Anlagen, als Hilfe beim Ersetzen der Stellvertreter.
const GENERIC_PLACEHOLDER_HINTS = [
  // Zusammengesetzt, damit ESLint (no-template-curly-in-string) nicht anschlägt.
  { system: '3CX', caller: ['$', '{caller_id}'].join('') },
  { system: 'Starface', caller: 'je nach Modul, siehe Handbuch „URL-Aufruf“' },
  { system: 'Placetel / sipgate', caller: 'Webhook mit Anrufernummer, siehe Anbieter-Doku' }
];

const CopyableUrl = ({ label, hint, url }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      setCopied(false);
    }
  };
  return (
    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">{label}</Typography>
          {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
        </Box>
        <Button size="small" onClick={copy} startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}>
          {copied ? 'Kopiert' : 'Kopieren'}
        </Button>
      </Stack>
      <Typography
        variant="body2"
        fontFamily="monospace"
        sx={{ mt: 1, wordBreak: 'break-all', color: 'text.secondary' }}
      >
        {url}
      </Typography>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {'swyx_netphone'|'generic_url'} props.provider
 * @param {Array} props.connectors  Live-Liste aus dem Abo der Einstellungsseite
 * @param {object} [props.rotated]  Ergebnis von rotateTelephonyUrlEndpoint: springt direkt
 *   zu „Adressen eintragen" für einen bestehenden Anschluss
 */
const TelephonyUrlSetupDialog = ({ open, onClose, provider, connectors, rotated = null }) => {
  const isSwyx = provider === 'swyx_netphone';
  const system = TELEPHONY_SYSTEMS[provider] || TELEPHONY_SYSTEMS.generic_url;

  const [activeStep, setActiveStep] = useState(0);
  const [displayName, setDisplayName] = useState('');
  const [extension, setExtension] = useState('');
  const [reportsConnect, setReportsConnect] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [endpoint, setEndpoint] = useState(null);
  const [issuedAt, setIssuedAt] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError('');
    setCreating(false);
    if (rotated) {
      setEndpoint(rotated);
      setIssuedAt(new Date());
      setActiveStep(2);
    } else {
      setEndpoint(null);
      setIssuedAt(null);
      setDisplayName('');
      setExtension('');
      setReportsConnect(false);
      setActiveStep(0);
    }
  }, [open, rotated]);

  const connector = useMemo(
    () => connectors.find((item) => item.id === endpoint?.connectorId) || null,
    [connectors, endpoint]
  );
  const lastEventAt = toDate(connector?.lastEventAt);
  const testSucceeded = Boolean(lastEventAt && issuedAt && lastEventAt >= issuedAt);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const result = await createTelephonyUrlEndpoint({ provider, displayName, extension, reportsConnect });
      setEndpoint(result);
      setIssuedAt(new Date());
      setActiveStep(2);
    } catch (callError) {
      setError(callError.message || 'Anschluss konnte nicht angelegt werden.');
    } finally {
      setCreating(false);
    }
  };

  const reportsConnectForEndpoint = rotated ? connector?.reportsConnect === true : reportsConnect;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{system.label}: Anrufe ans Portal melden</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical" sx={{ pt: 1 }}>
          <Step>
            <StepLabel>{STEPS[0]}</StepLabel>
            <StepContent>
              <Stack spacing={1.5}>
                {isSwyx ? (
                  <>
                    <Typography variant="body2">
                      Der NetPhone Client kann bei einem Anruf eine Web-Adresse aufrufen. Diese
                      Adresse tragen Sie in der <strong>Skin</strong> des Clients ein – es wird
                      nichts installiert.
                    </Typography>
                    <Typography variant="body2">Sie brauchen:</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 3, typography: 'body2' }}>
                      <li>das Recht, die Skin zu bearbeiten, oder Zugang zur NetPhone-Administration
                        (Skins werden dort je Benutzer zugewiesen),</li>
                      <li>die interne Durchwahl jedes Platzes, der Anrufe annimmt.</li>
                    </Box>
                    <Alert severity="info">
                      Bestehende Einträge anderer Programme (z. B. taxi.de) bleiben unverändert. Wir
                      nutzen eigene Ereignisse daneben.
                    </Alert>
                    <Alert severity="info">
                      Klingeln bei einem Anruf mehrere Plätze gleichzeitig, richten Sie jeden Platz
                      ein. Das Portal zeigt trotzdem nur einen Anrufhinweis.
                    </Alert>
                  </>
                ) : (
                  <>
                    <Typography variant="body2">
                      Ihre Telefonanlage muss bei einem eingehenden Anruf eine Web-Adresse aufrufen
                      können und dabei die Nummer des Anrufers einsetzen. Die Funktion heißt je nach
                      Hersteller „URL-Aufruf“, „CRM-Integration“ oder „Webhook“.
                    </Typography>
                    <Alert severity="info">
                      Klingeln mehrere Plätze gleichzeitig, reicht es, jeden Platz mit eigener Adresse
                      einzurichten. Das Portal zeigt nur einen Anrufhinweis.
                    </Alert>
                  </>
                )}
                <Box>
                  <Button variant="contained" onClick={() => setActiveStep(1)}>Weiter</Button>
                </Box>
              </Stack>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{STEPS[1]}</StepLabel>
            <StepContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  label="Name des Platzes"
                  placeholder="z. B. Dispo 1"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  fullWidth
                  autoFocus
                />
                <TextField
                  label="Interne Durchwahl (optional)"
                  placeholder="z. B. 21"
                  value={extension}
                  onChange={(event) => setExtension(event.target.value)}
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={reportsConnect}
                      onChange={(event) => setReportsConnect(event.target.checked)}
                    />
                  }
                  label="Die Anlage meldet auch, wenn ein Anruf angenommen wird"
                />
                <Typography variant="caption" color="text.secondary">
                  {isSwyx
                    ? 'Nur einschalten, wenn das Ereignis „Externe Verbindung aufgebaut“ frei ist. Nutzt es bereits ein anderes Programm (z. B. taxi.de), ausgeschaltet lassen – die Anrufliste zeigt dann „Annahme unbekannt“ statt „Verpasst“.'
                    : 'Nur einschalten, wenn Sie auch die Adresse für „Angenommen“ in der Anlage eintragen. Sonst zeigt die Anrufliste „Annahme unbekannt“ statt „Verpasst“.'}
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
                <Stack direction="row" spacing={1}>
                  <Button onClick={() => setActiveStep(0)}>Zurück</Button>
                  <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={creating || !displayName.trim()}
                    startIcon={creating ? <CircularProgress size={18} color="inherit" /> : null}
                  >
                    Anschluss anlegen
                  </Button>
                </Stack>
              </Stack>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{STEPS[2]}</StepLabel>
            <StepContent>
              {endpoint && (
                <Stack spacing={1.5}>
                  <Alert severity="warning">
                    Die Adressen enthalten den Zugangsschlüssel dieses Platzes und werden nur jetzt
                    angezeigt. Später erhalten Sie neue über „Adressen neu erzeugen“ – die alten
                    gelten dann nicht mehr.
                  </Alert>
                  <CopyableUrl
                    label="Klingeln"
                    hint={isSwyx ? 'Ereignis „Eingehender externer Ruf“' : 'Aufruf bei eingehendem Anruf'}
                    url={endpoint.eventUrls.ring}
                  />
                  {reportsConnectForEndpoint && (
                    <CopyableUrl
                      label="Angenommen"
                      hint={isSwyx ? 'Ereignis „Externe Verbindung aufgebaut“' : 'Aufruf, wenn der Anruf angenommen wird'}
                      url={endpoint.eventUrls.connect}
                    />
                  )}
                  <CopyableUrl
                    label="Aufgelegt"
                    hint={isSwyx ? 'Ereignis „Externe Verbindung beendet“' : 'Aufruf, wenn das Gespräch endet (optional)'}
                    url={endpoint.eventUrls.disconnect}
                  />

                  {isSwyx ? (
                    <>
                      <Typography variant="subtitle2" sx={{ pt: 1 }}>So tragen Sie die Adressen ein</Typography>
                      <Box component="ol" sx={{ m: 0, pl: 3, typography: 'body2', '& li': { mb: 0.75 } }}>
                        <li>Im NetPhone Client mit der rechten Maustaste auf die Oberfläche klicken und
                          die Skin zum <strong>Bearbeiten</strong> öffnen.</li>
                        <li>Die Skin sofort <strong>unter neuem Namen speichern</strong> (z. B.
                          „Dispo mit Anruferkennung“). So bleibt die bisherige Skin unverändert.</li>
                        <li>Die Eigenschaften der <strong>Web Extension</strong> öffnen – das ist der
                          Bereich, in dem auch der Eintrag von taxi.de steht.</li>
                        <li>Unter „Ereignisbehandlung“ das Ereignis <strong>„Eingehender externer
                          Ruf“</strong> wählen und die Adresse „Klingeln“ einfügen. Dann
                          <strong> „Externe Verbindung beendet“</strong> mit der Adresse „Aufgelegt“.</li>
                        <li>Den vorhandenen Eintrag „Externe Verbindung aufgebaut“ <strong>nicht
                          ändern</strong>, wenn ihn ein anderes Programm nutzt.</li>
                        <li>Speichern und die neue Skin zuerst <strong>einem Testbenutzer</strong>
                          {' '}zuweisen. Wenn der Test klappt, den übrigen Plätzen.</li>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Menünamen können je nach Version leicht abweichen. Die Platzhalter mit
                        Prozentzeichen (z. B. %RelatedLinePeernumber%) genau so übernehmen – der
                        Client ersetzt sie beim Anruf.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ pt: 1 }}>Stellvertreter ersetzen</Typography>
                      <Typography variant="body2">
                        Ersetzen Sie <code>&lt;ANRUFERNUMMER&gt;</code> durch die Variable Ihrer
                        Anlage für die Nummer des Anrufers. <code>&lt;ANGERUFENE_NUMMER&gt;</code> und
                        {' '}<code>&lt;ANRUF_ID&gt;</code> sind optional; ohne passende Variable
                        löschen Sie den jeweiligen Teil samt Parametername.
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 3, typography: 'body2' }}>
                        {GENERIC_PLACEHOLDER_HINTS.map((item) => (
                          <li key={item.system}><strong>{item.system}:</strong> {item.caller}</li>
                        ))}
                      </Box>
                    </>
                  )}
                  <Box>
                    <Button variant="contained" onClick={() => setActiveStep(3)}>Adressen sind eingetragen</Button>
                  </Box>
                </Stack>
              )}
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{STEPS[3]}</StepLabel>
            <StepContent>
              <Stack spacing={1.5}>
                <Typography variant="body2">
                  Rufen Sie jetzt von einem Handy die Zentrale an, sodass der eingerichtete Platz
                  klingelt. Sobald die Meldung ankommt, erscheint hier eine Bestätigung.
                </Typography>
                {testSucceeded ? (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    Anruf erkannt ({lastEventAt.toLocaleTimeString('de-DE')}). Die Anruferkennung an
                    diesem Platz funktioniert.
                  </Alert>
                ) : (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Warte auf den ersten Anruf …</Typography>
                  </Stack>
                )}
                {!testSucceeded && (
                  <Typography variant="caption" color="text.secondary">
                    Kommt nichts an: Ist die neue Skin dem Benutzer zugewiesen und im Client geladen?
                    Stehen die Adressen vollständig und beim richtigen Ereignis?
                  </Typography>
                )}
              </Stack>
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant={testSucceeded ? 'contained' : 'text'}>
          {testSucceeded ? 'Fertig' : 'Schließen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TelephonyUrlSetupDialog;
